/**
 * Meraki devices store — per-network device list + bulk-edit staging.
 *
 * Edits are staged client-side (`pending`) until the user clicks Apply.
 * Apply POSTs the diff and refreshes the list.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import { DeviceRowListSchema, type DeviceRow } from "@/api/schemas/meraki";
import { MOCK_DEVICE_ROWS } from "@/api/fixtures/meraki-devices";

/** A staged edit — partial DeviceRow keyed by serial. */
export type DeviceEdit = Partial<Pick<DeviceRow, "name" | "tags" | "notes">>;

interface State {
  data: DeviceRow[];
  pending: Record<string, DeviceEdit>;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  loadFor: (networkId: string) => Promise<void>;
  /** Stage a single field change for a device. */
  stage: (serial: string, change: DeviceEdit) => void;
  /** Drop staged edits without saving. */
  reset: () => void;
  /** Push staged edits to the server. */
  apply: (networkId: string) => Promise<void>;
}

export const useMerakiDevices = create<State>((set, get) => ({
  data: [],
  pending: {},
  status: "idle",
  error: null,

  loadFor: async (networkId) => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/devices/", { network_id: networkId });
      set({ data: DeviceRowListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_DEVICE_ROWS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  stage: (serial, change) =>
    set((s) => ({
      pending: { ...s.pending, [serial]: { ...s.pending[serial], ...change } },
    })),

  reset: () => set({ pending: {} }),

  apply: async (networkId) => {
    const edits = get().pending;
    const payload = Object.entries(edits).map(([serial, change]) => ({ serial, ...change }));
    if (payload.length === 0) return;
    await apiPost("/meraki/devices/bulk-update/", { network_id: networkId, updates: payload });
    set({ pending: {} });
    await get().loadFor(networkId);
  },
}));
